import { Component, Inject, Input, OnDestroy, OnInit, Type } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { trigger, state, style, transition, animate, AnimationEvent } from '@angular/animations';

import { Observable, BehaviorSubject, Subscription } from 'rxjs';

import { LoginApi } from '../../../../../api/login/login.api';

import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { UserStateModel } from '../../../../../Core/Users/models/user-state.model';
import { AccountModel } from '../../../../../Core/Users/models/account.model';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';

import { NavItemInternal, NavItemType, NavItemUpsellPopup } from '../navbar.component';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { UpsellDialogType, UpsellInfoDialogComponent } from '../../../../../Common/Dialogs/components/UpsellInfoDialog/upsell-info-dialog.component';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { HubspotFormService } from '../../../../Misc/services/hubspot-form.service';
import { ConfirmDialogComponent } from '../../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';

@Component({
    selector: 'agpr-compressed-nav',
    styleUrls: ['./compressed-nav.component.scss'],
    templateUrl: './compressed-nav.component.html',
    animations: [
        trigger('subMenuContent', [
            state('hidden', style({
                height: 0,
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', animate('300ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})
export class CompressedNavComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() menuItems$: Observable<NavItemInternal[][]>;
    @Input() userState: UserStateModel;
    @Input() userAccounts: AccountModel[];

    menuItems: NavItemInternal[][];
    menuItemsSubscription: Subscription;
    hideSlideOverlay: boolean = false;
    searchTerm?: string;
    originalAccounts: AccountModel[] = [];

    readonly navTypes = NavItemType;
    // Matches the dimensions of AnalysisUpsellPopupComponent so it can be positioned against the cursor
    readonly upsellPopupWidth = 485;
    readonly upsellPopupHeight = 372;
    readonly messageSeverity = messageSeverityEnum;

    loading: boolean = false;

    switchAccountId: number;

    constructor(
        public coreDialogService: CoreDialogService,
        public loginApi: LoginApi,
        public currentUserStateService: CurrentUserStateService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private hubspotFormService: HubspotFormService
    ) {
        super(coreDialogService, 'CompressedNav');
    }

    ngOnInit() {
        this.menuItemsSubscription = this.menuItems$.subscribe(items => {
            this.menuItems = items;
            if (this.menuItems.length === 0) {
                this.close();
            }
            this.originalAccounts = this.userAccounts;
        });
    }

    close() {
        this.menuItems.forEach(section => {
            section.forEach(mainItem => {
                if (!!mainItem.items) {
                    mainItem.collapsed = true;
                    mainItem.items.forEach(subItem => {
                        if (!!subItem.items) {
                            subItem.collapsed = true;
                        }
                    });
                }
            });
        });
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    toggleNavItem(item: NavItemInternal) {
        if (item.collapsed) {
            this.menuItems.forEach(section => {
                section.forEach(mainItem => {
                    if (item.id !== mainItem.id && !mainItem.items?.includes(item)) {
                        mainItem.collapsed = true;
                    }
                    if (mainItem.items) {
                        mainItem.items.forEach(subItem => {
                            if (subItem.collapsed !== undefined && subItem.id !== item.id) {
                                subItem.collapsed = true;
                            }
                        });
                    }
                });
            });
        }
        item.collapsed = !item.collapsed;
    }

    openDialog(dialogReference: Type<AbstractDialogComponent> | string) {
        this.close();
        this.coreDialogService.open(dialogReference);
    }

    openTakeOverPanel(component) {
        this.close();
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer
            }
        };
        this.coreDialogService.open(component, io);
    }

    openUpsellInfoPanel(item: NavItemInternal) {
        this.close();
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        let type: UpsellDialogType;
        switch (item.id) {
            case 'primaryNavMediaIntelligence':
                type = UpsellDialogType.monitor;
                break;
            case 'primaryNavMediaRelations':
                type = UpsellDialogType.discover;
                break;
            case 'primaryNavSocial':
                type = UpsellDialogType.social;
                break;
        }
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                type: type
            }
        };
        this.coreDialogService.open(UpsellInfoDialogComponent, io);
    }

    /**
     * Sub items whose feature is not provisioned stay in the menu behind a lock and open an upsell
     * popup instead of navigating. The popup lives outside of Common, so it is opened by its map key.
     */
    openSubItemUpsell(subItem: NavItemInternal, event: Event) {
        event.preventDefault();
        event.stopPropagation();

        if (!subItem.upsellPopup) {
            return;
        }

        this.close();

        // TODO: NEP-38233 - remove this and requestUnlockDirectly() once we have marketing message
        // content for Visibility Intelligence
        if (subItem.upsellPopup.featureName === 'Visibility Intelligence') {
            this.requestUnlockDirectly(subItem.upsellPopup);
            return;
        }

        const ioModel = this.coreDialogService.getIoConfigCentered(this.upsellPopupWidth, this.upsellPopupHeight);

        // Anything the config leaves out keeps the popup's own default
        Object.entries(subItem.upsellPopup).forEach(([input, value]) => {
            if (value !== undefined) {
                ioModel.inputs[input] = value;
            }
        });

        this.coreDialogService.open('AnalysisUpsellPopupComponent', ioModel);
    }

    /**
     * Bypasses AnalysisUpsellPopupComponent's informational content entirely and requests the
     * unlock right away instead — used for Visibility Intelligence only.
     */
    private requestUnlockDirectly(upsellPopup: NavItemUpsellPopup) {
        if (!upsellPopup.hubspotFormType || !upsellPopup.featureName) {
            return;
        }

        this.hubspotFormService.requestUnlock(upsellPopup.hubspotFormType, upsellPopup.featureName).then(submitted => {
            if (submitted) {
                this.coreDialogService.open(
                    ConfirmDialogComponent,
                    {
                        inputs: {
                            heading: upsellPopup.confirmationHeading,
                            messageHtml: upsellPopup.confirmationMessage,
                            showCancel: false,
                            width: 600
                        }
                    }
                );
            }
        }).catch(() => {
            window.open('https://www.agilitypr.com/contact/');
        });
    }

    openIntercom() {
        this.close();
        window.Intercom('show');
    }

    switchAccount(account: AccountModel) {
        this.loading = true;
        this.switchAccountId = account.id;

        this.currentUserStateService.switchAccount(account.id).subscribe({
            next: (token) => {
                if (this.currentUserStateService.accountHasJournodex(account)) {
                    this.currentUserStateService.navigateToJournodex();
                    return;
                }
                window.location.href = window.location.origin + this.appBaseHref + 'Dashboard';
            },
            error: (error) => {
                this.loading = false;
            }
        });
    }

    filterAccounts() {
        const searchTerm = this.searchTerm?.toLowerCase();

        this.userAccounts = this.originalAccounts
            .filter(a => a.name.toLowerCase().includes(searchTerm))
            .sort((a, b) => {
                if ((a.active && !a.isExpiryDatePast) !== (b.active && !b.isExpiryDatePast)) {
                    return (a.active && !a.isExpiryDatePast) ? -1 : 1;
                }
                if (a.name.toLowerCase().startsWith(searchTerm) !== b.name.toLowerCase().startsWith(searchTerm)) {
                    return a.name.toLowerCase().startsWith(searchTerm) ? -1 : 1;
                }

                if (a.name.toLowerCase().includes(searchTerm) !== b.name.toLowerCase().includes(searchTerm)) {
                    return a.name.toLowerCase().includes(searchTerm) ? -1 : 1;
                }

                return a.name.localeCompare(b.name);
            });
    }

    animationDone(event: AnimationEvent) {
        if (event.toState === 'hidden') {
            (event.element as HTMLElement).style.display = 'none';
        }
    }

    ngOnDestroy(): void {
        this.menuItemsSubscription.unsubscribe();
    }
}
